// src/pages/EditorPage.jsx
import React, { useState, useRef } from 'react';
import { X, Save, Loader, Edit3, Type } from 'lucide-react';
import { usePages } from '../hooks/usePages';
import ComponentLibrary from '../components/editor/ComponentLibrary';
import EditorComponentItem, { componentTypes } from '../components/editor/EditorComponentItem';
import PreviewArea from '../components/editor/PreviewArea';
import EnlargedImageModal from '../components/ui/EnlargedImageModal';

export default function EditorPage({ currentEditPage, onBack, onSaveSuccess }) {
  const [components, setComponents] = useState(currentEditPage?.components || []);
  const [pageTitle, setPageTitle] = useState(currentEditPage?.title || '');
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const dragRef = useRef(null);
  const { savePage, loading } = usePages();

  const addComponent = (type) => {
    const def = componentTypes.find(c => c.type === type);
    const newComponent = {
      id: Date.now() + Math.random(),
      type,
      content: def.defaultContent
    };
    setComponents([...components, newComponent]);
  };

  const deleteComponent = (id) => {
    setComponents(components.filter(c => c.id !== id));
  };

  const updateComponentContent = (id, content) => {
    setComponents(components.map(c => c.id === id ? { ...c, content } : c));
  };

  const handleDragStart = (e, component) => {
    setDraggedItem(component);
    e.dataTransfer.effectAllowed = 'move';
    const target = e.target.closest('.draggable-component');
    if (target) {
      target.style.opacity = '0.5';
      target.style.transform = 'scale(0.98)';
    }
  };

  const handleDragEnd = (e) => {
    setDraggedItem(null);
    const target = e.target.closest('.draggable-component');
    if (target) {
      target.style.opacity = '1';
      target.style.transform = 'scale(1)';
    }
  };

  const handleDragOverContainer = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropContainer = (e) => {
    e.preventDefault();
    if (!draggedItem || !dragRef.current) return;
    const wrappers = Array.from(dragRef.current.querySelectorAll('.draggable-component-wrapper'));
    const mouseY = e.clientY;
    let dropIndex = components.length;
    for (let i = 0; i < wrappers.length; i++) {
      const rect = wrappers[i].getBoundingClientRect();
      if (mouseY < rect.top + rect.height / 2) {
        dropIndex = i;
        break;
      }
    }
    const newComponents = [...components];
    const draggedIndex = newComponents.findIndex(c => c.id === draggedItem.id);
    if (draggedIndex === -1) return;
    if (draggedIndex === dropIndex || (draggedIndex < dropIndex && draggedIndex === dropIndex - 1)) {
      setDraggedItem(null);
      return;
    }
    const [item] = newComponents.splice(draggedIndex, 1);
    newComponents.splice(dropIndex, 0, item);
    setComponents(newComponents);
    setDraggedItem(null);
  };

  const handleSave = async () => {
    if (!pageTitle.trim()) {
      alert('请输入页面标题');
      return;
    }
    if (components.length === 0) {
      alert('请至少添加一个组件');
      return;
    }
    const success = await savePage(pageTitle, components, currentEditPage?.id);
    if (success) {
      onSaveSuccess();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="text-gray-600 hover:text-gray-900 flex items-center space-x-1"
              >
                <X className="w-5 h-5" />
                <span>返回</span>
              </button>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Type className="w-5 h-5 text-white" />
                </div>
                <h1 className="text-xl font-bold text-gray-900">
                  {currentEditPage ? '编辑页面' : '新建页面'}
                </h1>
              </div>
            </div>
            <button
              onClick={handleSave}
              disabled={loading || components.length === 0}
              className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              {loading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>保存中...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>保存页面</span>
                </>
              )}
            </button>
          </div>
        </div>
      </nav>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1">
            <ComponentLibrary onAdd={addComponent} />
          </div>
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">页面标题 *</label>
              <input
                type="text"
                value={pageTitle}
                onChange={(e) => setPageTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="用于管理页面的标题"
              />
              <p className="text-xs text-gray-500 mt-1">
                这个标题仅用于在管理页面中识别页面，不会显示在生成的页面中
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Edit3 className="w-5 h-5 mr-2 text-gray-600" />
                  页面编辑区
                </h2>
                {components.length > 0 && (
                  <span className="text-sm text-gray-500">{components.length} 个组件 - 拖拽可调整顺序</span>
                )}
              </div>
              {components.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Type className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">开始创建页面</h3>
                  <p className="text-gray-500 mb-6">从左侧组件库中添加组件，然后拖拽调整顺序</p>
                </div>
              ) : (
                <div
                  ref={dragRef}
                  className="space-y-3 min-h-96 relative"
                  onDragOver={handleDragOverContainer}
                  onDrop={handleDropContainer}
                >
                  {components.map((comp) => (
                    <div key={comp.id} className="draggable-component-wrapper">
                      <EditorComponentItem
                        component={comp}
                        onDelete={deleteComponent}
                        onUpdate={updateComponentContent}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <PreviewArea components={components} onImageClick={setEnlargedImage} />
          </div>
        </div>
      </div>
      <EnlargedImageModal src={enlargedImage} onClose={() => setEnlargedImage(null)} />
    </div>
  );
}